import {
	ATTRIBUTES,
	CartItem,
	cartItemIsTrial,
	getItemQuantity,
	PUB_SUB_EVENTS,
	publish,
	rectifyItemQuantity,
	SELECTORS,
	subscribe,
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type CartDrawer } from '@/scripts/cart/cart-drawer'
import { QuickAddModal } from '@/scripts/optional/quick-add'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { BundleChild, RefillBundleChild } from '@/scripts/product/types'
import { type PdpUpsellModal } from '@/scripts/cart/pdp-upsell-modal'

export type PriceBreakdownData = {
	// based on app code. quantity breaks are stored in a tuple array.
	// [subscription_flat_discount, otp_volume_discount, sub_volume_discount]
	// each discount is an int value representing the discount applied to each item.
	// i.e. a discount of $10 would be reflected as 1000, just like a liquid currency value
	quantityBreaks: [number, number, number][]
	refillQuantityBreaks: number[]
	default: {
		pricePerUnit: number
		subscriptionPricePerUnit: number
		refillCostPerUnit: number
	}
}

export interface BundleConfigRaw {
	bundleChildren: Array<{
		variant_id: number
		selling_plan_id?: number
		max_amount_in_cart?: number
	}>
	isBundleItem: boolean
}

export interface RefillBundleConfigRaw {
	bundleChildren: Array<{
		parent_variant_id: number
		refill_variant_id?: number
		selling_plan_id?: number
		disable_selling_plan_when_refill: boolean
	}>
	isBundleItem: boolean
}

export function isBundleConfigRaw(i: unknown): i is BundleConfigRaw {
	if (typeof i !== 'object' || i === null) {
		return false
	}
	if (!Array.isArray((i as BundleConfigRaw).bundleChildren)) {
		return false
	}
	return typeof (i as BundleConfigRaw).isBundleItem === 'boolean'
}

export function isRefillBundleConfigRaw(
	i: unknown
): i is RefillBundleConfigRaw {
	if (typeof i !== 'object' || i === null) {
		return false
	}
	if (!Array.isArray((i as RefillBundleConfigRaw).bundleChildren)) {
		return false
	}
	return typeof (i as RefillBundleConfigRaw).isBundleItem === 'boolean'
}
export interface BundleConfig {
	bundleChildren: BundleChild[]
	isBundleItem: boolean
}

export interface RefillBundleConfig {
	bundleChildren: RefillBundleChild[]
	isBundleItem: boolean
}

export class ProductForm extends UcoastEl {
	static htmlSelector = 'product-form'
	static selectors = {
		form: 'form',
		formIdEl: '[name=id]',
		variantId: '[data-uc-product-variant-id], [name=id]',
		submitButton: '[type="submit"]',
		submitButtonMessage: '[type="submit"] span',
		soldOutMessage: '[data-uc-sold-out-message]',
		errorMessageWrapper: '[data-uc-product-form-error-message-wrapper]',
		errorMessage: '[data-uc-product-form-error-message]',
		priceBreakdown: '[data-uc-product-price-breakdown]',
		bundle: '[data-uc-product-variant-bundle]',
		sellingPlan: '[data-uc-selling-plan-id]',
		refillData: '[data-uc-refill-data]',
	}

	quantityInput: HTMLInputElement
	form: HTMLFormElement
	variantId: HTMLInputElement
	source: string
	formId: string
	formIdEl: HTMLInputElement
	cart: CartDrawer
	submitButton: HTMLButtonElement
	errorMessageWrapper?: HTMLElement
	errorMessage?: HTMLElement
	hideErrors: boolean
	error?: boolean
	priceBreakdownData: PriceBreakdownData = {
		default: {
			pricePerUnit: 0,
			subscriptionPricePerUnit: 0,
			refillCostPerUnit: 0,
		},
		quantityBreaks: [[0, 0, 0]],
		refillQuantityBreaks: [0],
	}
	bundleConfigByVariantId: Record<string, BundleConfig> = {}
	bundleConfigByRefillVariantId: Record<string, RefillBundleConfig> = {}
	disableSellingPlanForRefill: boolean
	upsellFromVariant?: string
	variantChangeUnsubscriber: () => void
	variantChangeListenerUnsubscriber: () => void
	cartChangeUnsubscriber: () => void
	modal?: PdpUpsellModal

	override connectedCallback() {
		super.connectedCallback()
		this.updatePriceValues()
	}
	override disconnectedCallback() {
		super.disconnectedCallback()
		this.variantChangeUnsubscriber?.()
		this.variantChangeListenerUnsubscriber?.()
		this.cartChangeUnsubscriber?.()
	}

	constructor() {
		super()
		if (this.hasAttribute('data-redirect-to-modal')) {
			this.modal = q.os<PdpUpsellModal>('pdp-upsell-modal')
		}
		this.form = q.rs(ProductForm.selectors.form, this)
		this.formId = q.ra(this.form, 'id')
		this.formIdEl = q.rs(ProductForm.selectors.formIdEl, this.form)
		this.quantityInput = q.rs(
			`[data-uc-quantity-input][form="${this.formId}"]`
		)
		this.quantityInput.value = q.ra(this.quantityInput, 'value')
		this.disableSellingPlanForRefill = this.hasAttribute(
			'data-uc-selling-plan-disabled-for-refill'
		)
		this.upsellFromVariant = q.oa(this, 'data-upsell-variant-id')
		const priceBreakdown = q.rs(ProductForm.selectors.priceBreakdown, this)
		try {
			this.priceBreakdownData = JSON.parse(priceBreakdown.innerText)
		} catch (err) {
			console.error(err)
		}

		try {
			const bundleData = q.os(ProductForm.selectors.bundle, this)
			const parsedBundleDataByVariantId = JSON.parse(
				bundleData?.innerText ?? '{}'
			)

			Object.entries(parsedBundleDataByVariantId).forEach(
				([variantId, configRaw]) => {
					if (!isBundleConfigRaw(configRaw)) {
						return
					}
					const config: BundleConfig = {
						bundleChildren: [],
						isBundleItem: configRaw.isBundleItem,
					}
					configRaw.bundleChildren.forEach((configItemRaw) => {
						config.bundleChildren.push({
							variantId: configItemRaw.variant_id,
							sellingPlanId: configItemRaw.selling_plan_id,
							maxAmountInCart: configItemRaw.max_amount_in_cart,
						})
					})
					this.bundleConfigByVariantId[variantId] = config
				}
			)

			const refillData = q.os(ProductForm.selectors.refillData, this)
			const parsedRefillData = JSON.parse(refillData?.innerText ?? '{}')

			Object.entries(parsedRefillData).forEach(
				([variantId, refillConfigRaw]) => {
					if (!isRefillBundleConfigRaw(refillConfigRaw)) {
						return
					}
					const config: RefillBundleConfig = {
						bundleChildren: [],
						isBundleItem: refillConfigRaw.isBundleItem,
					}
					refillConfigRaw.bundleChildren.forEach((configItemRaw) => {
						config.bundleChildren.push({
							parent_variant_id: configItemRaw.parent_variant_id,
							refill_variant_id: configItemRaw.refill_variant_id,
							selling_plan_id: configItemRaw.selling_plan_id,
							disable_selling_plan_when_refill:
								configItemRaw.disable_selling_plan_when_refill,
						})
					})
					this.bundleConfigByRefillVariantId[variantId] = config
				}
			)
		} catch (err) {
			console.error(err)
		}

		this.formIdEl.disabled = false
		this.variantId = q.rs(ProductForm.selectors.variantId, this)
		this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
		this.quantityInput.addEventListener(
			'change',
			this.updatePriceValues.bind(this)
		)
		this.variantChangeUnsubscriber = subscribe(
			PUB_SUB_EVENTS.variantChange,
			this.updatePriceValues.bind(this)
		)
		this.variantChangeListenerUnsubscriber = subscribe(
			PUB_SUB_EVENTS.variantChange,
			this.variantChangeListener.bind(this)
		)
		this.cartChangeUnsubscriber = subscribe(
			PUB_SUB_EVENTS.cartUpdate,
			this.rectifyAddToCartButtonStatus.bind(this)
		)
		this.cart = q.rs<CartDrawer>('cart-drawer')
		this.submitButton = q.rs(ProductForm.selectors.submitButton, this)
		this.source = q.ra(this, 'data-uc-product-form-source')
		if (this.cart?.localName === 'cart-drawer')
			this.submitButton.setAttribute('aria-haspopup', 'dialog')

		this.hideErrors = this.dataset.hideErrors === 'true'
	}

	get quantity(): number {
		return parseInt(this.quantityInput.value)
	}

	variantChangeListener(event: { data: { variant: { title: string } } }) {
		if (this.classList.contains('has-external-atc-redirect')) {
			const selectedRadioButton = document.querySelector<HTMLInputElement>('[data-uc-product-variant-color]:checked')
			const externalAtc = document.querySelector<HTMLAnchorElement>('.external-atc')
			if (selectedRadioButton?.value === event.data.variant.title && selectedRadioButton.dataset.atcRedirectUrl) {
				this.classList.add('hidden')
				if (externalAtc) {
					externalAtc.classList.remove('hidden')
					externalAtc.setAttribute('href', selectedRadioButton.dataset.atcRedirectUrl ?? '')
					externalAtc.innerText = selectedRadioButton.dataset.atcRedirectLabel ?? ''
				}
			} else {
				this.classList.remove('hidden')
				if (externalAtc) {
					externalAtc.classList.add('hidden')
				}
			}
		}
	}

	// TODO: move this to selling-plan.ts and use pub/sub
	updatePriceValues() {
		// update prices
		console.log('this.priceBreakdownData', this.priceBreakdownData)
		const breaks = this.priceBreakdownData.quantityBreaks ?? [[0, 0, 0]]

		console.log({ breaks })
		const quantity = this.quantity
		let currentBreak: [number, number, number] = [0, 0, 0]
		if (quantity >= breaks.length) {
			currentBreak = breaks[breaks.length - 1]
		} else {
			currentBreak = breaks[quantity - 1]
		}

		const refillValues = this.priceBreakdownData.refillQuantityBreaks
			?.length
			? [
					this.priceBreakdownData.default.refillCostPerUnit,
					...this.priceBreakdownData.refillQuantityBreaks,
				]
			: [this.priceBreakdownData.default.refillCostPerUnit]
		let refillCost = this.priceBreakdownData.default.refillCostPerUnit
		console.log({ refillValues })
		console.log(
			'(this.priceBreakdownData.refillQuantityBreaks',
			this.priceBreakdownData.refillQuantityBreaks
		)
		if (refillValues && refillValues.length) {
			if (quantity >= refillValues.length) {
				refillCost = refillValues[refillValues.length - 1]
			} else {
				refillCost = refillValues[quantity - 1]
			}
		}

		const initialOtpUnit = this.priceBreakdownData.default.pricePerUnit
		// second el in currentBreak is the volume discount for OTP at this quantity
		const discountedOtpUnit = initialOtpUnit - currentBreak[1]
		// first el in currentBreak is a flat subscription discount
		// third el in currentBreak is the volume discount for SUB at this quantity
		const subPriceUnit =
			this.priceBreakdownData.default.subscriptionPricePerUnit -
			currentBreak[0] -
			currentBreak[2]

		const initialOtpPrice = ((initialOtpUnit * quantity) / 100).toFixed(2)
		const discountedOtpPrice = (
			(discountedOtpUnit * quantity) /
			100
		).toFixed(2)
		const subPrice = ((subPriceUnit * quantity) / 100).toFixed(2)

		if (initialOtpUnit === 0) {
			return
		}

		const compareAt = q.os('[data-uc-selling-plan-compare-at-price]', this)
		// Try Before You Buy
		if (subPriceUnit === 0) {
			// temporary hard-coded the solution here (as we are "trailing" the trial)
			if (compareAt) {
				compareAt.innerHTML = quantity === 1 ? '$99' : '$148'
			}
		}

		// change selling plan prices
		const sellingPlans = q.ol(ProductForm.selectors.sellingPlan, this) ?? []
		sellingPlans.forEach((el) => {
			const id = q.oa(el, 'data-uc-selling-plan-id')
			const tbybPresentation = q.oa(el, 'data-tbyb-presentation')
			console.log({ tbybPresentation })
			const priceTagEl = el.querySelector(
				'[data-uc-selling-plan-price-value]'
			)
			if (!priceTagEl) {
				return
			}
			const compareAt = q.os(
				'[data-uc-selling-plan-compare-at-price]',
				el
			)

			const percentEl = q.os('[data-uc-selling-plan-percent]', el)
			if (tbybPresentation === 'otp') {
				if (compareAt) {
					// before fix
					//compareAt.innerHTML = `$${initialOtpPrice}`
					// after fix
					compareAt.classList.add('hidden')
					priceTagEl.innerHTML = `$${initialOtpPrice}`
				}
			} else if (tbybPresentation === 'subscription') {
				if (compareAt) {
					compareAt.innerHTML = `$${initialOtpPrice}`
				}
			}
			if (tbybPresentation === 'otp' && compareAt) {
				// tbyb otp
			} else {
				// normal otp
				const activePrice = id === 'otp' ? discountedOtpPrice : subPrice
				priceTagEl.innerHTML = `$${activePrice}`
				if (compareAt) {
					compareAt.innerHTML = `$${initialOtpPrice}`
					if (parseFloat(activePrice) < parseFloat(initialOtpPrice)) {
						compareAt.classList.remove('hidden')
					} else {
						compareAt.classList.add('hidden')
					}
				}

				if (percentEl) {
					const difference =
						parseFloat(initialOtpPrice) - parseFloat(subPrice)
					const percent =
						(difference / parseFloat(initialOtpPrice)) * 100
					percentEl.innerHTML = `${percent.toFixed(0)}`
				}
				// Get filters per delivery from the element's data attribute
				const filtersPerDelivery = parseInt(
					el.getAttribute('data-filters-per-delivery') || '1'
				)
				const totalFilters = quantity * filtersPerDelivery
				const isPlural = totalFilters > 1
				const description = `${totalFilters} fresh filter${isPlural ? 's' : ''} sent every 90 days for only $${(refillCost / 100).toFixed(2)}${isPlural ? ' each' : ''}. Skip or cancel anytime.`
				const descriptionEl = el.querySelector(
					'[data-uc-selling-plan-description]'
				)
				if (descriptionEl && description) {
					descriptionEl.innerHTML = description
				}
			}
		})

		// update the refill variant selling plan attribute based on variant setting
		const refillData =
			this.bundleConfigByRefillVariantId[this.variantId.value]
		if (!refillData) return
		if (
			refillData.bundleChildren.find(
				(child) => child.disable_selling_plan_when_refill
			)
		) {
			this.disableSellingPlanForRefill = true
		} else {
			this.disableSellingPlanForRefill = false
		}
	}

	getSpinner() {
		return q.rs(SELECTORS.loadingOverlaySpinner, this)
	}

	onSubmitHandler(event: Event) {
		event.preventDefault()
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return

		this.handleErrorMessage()

		this.submitButton.setAttribute('aria-disabled', 'true')
		this.submitButton.setAttribute(ATTRIBUTES.loading, '')
		this.getSpinner().classList.remove('hidden')

		const formData = new FormData(this.form)

		if (this.cart) {
			const sectionIdsToRender = this.cart
				.getSectionsToRender()
				.filter((section) => section.id)
				.map((section) => section.id)
			if (!sectionIdsToRender.length)
				throw Error('getSectionsToRender returned empty array')
			formData.append('sections', sectionIdsToRender.join(','))
			formData.append('sections_url', window.location.pathname)
			if (!(document.activeElement instanceof HTMLElement))
				throw Error('no activeElement')
			this.cart.setActiveElement(document.activeElement)
		}

		const addedVariantId = formData.get('id') as string

		if (!addedVariantId) throw Error('No variant id found')

		let items = rectifyItemQuantity(formData, this.quantity)
		items = this.rectifyBundleItems(items)
		items = this.rectifyRefillItems(items)

		// TODO: danny hot times
		// const upsellVariantId = q.oa(
		// 	'data-upsell-variant-id',
		// 	this.form
		// )
		if (this.upsellFromVariant && this.variantId.value) {
			items = this.addUpsellTracking(
				items,
				this.upsellFromVariant,
				this.variantId.value
			)
		}
		// TODO: danny hot times

		fetch(`${window.routes.cart_add_url}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: `application/json`,
				'X-Requested-With': 'XMLHttpRequest',
			},
			body: JSON.stringify({
				items: items,
				form_type: q.rfd(formData, 'form_type'),
				sections: q.ofd(formData, 'sections'),
				sections_url: q.ofd(formData, 'sections_url'),
			}),
		})
			.then((response) => response.json())
			.then((response) => {
				if (response.status || response.errors) {
					publish(PUB_SUB_EVENTS.cartError, {
						source: 'product-form',
						productVariantId: addedVariantId,
						errors: response.errors || response.description,
						message: response.message,
					})
					this.handleErrorMessage(response.description)

					const soldOutMessage = this.submitButton.querySelector(
						ProductForm.selectors.soldOutMessage
					)
					if (!soldOutMessage) return
					this.submitButton.setAttribute('aria-disabled', 'true')
					const submitButtonText = q.rs(
						ProductForm.selectors.submitButtonMessage,
						this.submitButton
					)
					submitButtonText.classList.add('hidden')
					soldOutMessage.classList.remove('hidden')
					this.error = true
					return
				} else if (!this.cart) {
					window.location = window.routes.cart_url
					return
				}

				if (!this.error)
					publish(PUB_SUB_EVENTS.cartUpdate, {
						source: 'product-form',
						productVariantId: addedVariantId,
					})
				this.error = false

				// note - i dont think this quickAddModal code is used for anything

				if (this.modal) {
					const variantId = items[0]?.id
					const sellingPlanId = items[0]?.selling_plan
					const productHandle = q.oa(this, 'data-product-handle')
					if (variantId && productHandle) {
            this.modal.atcCartResponse = response
						void this.modal.open(
							{
								productHandle,
								variantId,
								sellingPlanId,
							},
							event.currentTarget as HTMLElement | undefined
						)
					}
				} else {
					this.cart.renderContents(response)
				}
			})
			.catch((e) => {
				console.error(e)
			})
			.finally(() => {
				this.submitButton.removeAttribute(ATTRIBUTES.loading)
				this.cart?.hasAttribute(ATTRIBUTES.cartEmpty) &&
					this.cart.removeAttribute(ATTRIBUTES.cartEmpty)

				this.rectifyAddToCartButtonStatus()

				if (!this.error)
					this.submitButton.removeAttribute('aria-disabled')
				this.getSpinner().classList.add('hidden')
			})
	}

	handleErrorMessage(errorMessage?: string) {
		if (this.hideErrors) return

		this.errorMessageWrapper =
			this.errorMessageWrapper ||
			q.rs(ProductForm.selectors.errorMessageWrapper, this)
		if (!this.errorMessageWrapper)
			throw new Error('No error message wrapper found')
		this.errorMessage =
			this.errorMessage ||
			q.rs(ProductForm.selectors.errorMessage, this.errorMessageWrapper)

		this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage)

		if (errorMessage) {
			this.errorMessage.textContent = errorMessage
		}
	}

	rectifyAddToCartButtonStatus() {
		const formData = new FormData(this.form)

		const variantId = (formData.get('id') as string) ?? ''
		const sellingPlanId = formData.get('selling_plan') as string | undefined
		const quantityInCart = getItemQuantity(variantId, sellingPlanId)
		const isTrial = cartItemIsTrial(variantId, sellingPlanId)

		if (quantityInCart > 0 && isTrial) {
			// hard-coded temporary solution to limit the trial purchasable quantity to 1
			this.setAddToCartButtonDisabled(true)
		} else {
			this.setAddToCartButtonDisabled(false)
		}
	}
	setAddToCartButtonDisabled(disabled: boolean) {
		this.submitButton.disabled = disabled
	}
	rectifyBundleItems(items: CartItem[]): CartItem[] {
		const mergedItems = items.reduce((acc, item) => {
			const existingItem = acc.find(
				(i) => i.id === item.id && i.selling_plan === item.selling_plan
			)
			if (existingItem) {
				existingItem.quantity += item.quantity
			} else {
				acc.push(item)
			}
			return acc
		}, [] as CartItem[])
		const maxAmountByVariantId: Record<string, number> = {}
		const newBundleItems = mergedItems.reduce((accum, curr) => {
			const bundleConfigs = this.bundleConfigByVariantId[curr.id] ?? {
				bundleChildren: [],
			}
			bundleConfigs.bundleChildren.forEach((config) => {
				maxAmountByVariantId[config.variantId] = Math.min(
					config.maxAmountInCart ?? Infinity,
					maxAmountByVariantId[config.variantId] ?? Infinity
				)
				const existingItem = accum.find(
					(i) =>
						i.id === config.variantId &&
						i.selling_plan === config.sellingPlanId
				)
				if (existingItem) {
					existingItem.quantity =
						existingItem.quantity + curr.quantity
				} else {
					accum.push({
						id: config.variantId,
						quantity: curr.quantity,
						selling_plan: config.sellingPlanId,
					})
				}
			})
			return accum
		}, [] as CartItem[])
		const bundleItemsToAdd = newBundleItems
			.map((item) => {
				const currentQuantity = getItemQuantity(
					item.id.toString(),
					item.selling_plan?.toString()
				)
				const additionalQuantity = item.quantity
				const max = maxAmountByVariantId[item.id] ?? Infinity
				if (currentQuantity + additionalQuantity >= max) {
					const toAdd = max - currentQuantity
					return {
						...item,
						quantity: toAdd,
					}
				}
				return {
					...item,
					quantity: item.quantity,
				}
			})
			.filter((item) => item.quantity > 0)

		return [...mergedItems, ...bundleItemsToAdd]
	}

	getCoreItemWithParentVariantId(items: CartItem[]) {
		return items.find(
			(item) =>
				item.selling_plan && this.bundleConfigByRefillVariantId[item.id]
		)
	}

	rectifyRefillItems(items: CartItem[]): CartItem[] {
		const formData = new FormData(this.form)
		const tbyb = formData.get('tbyb') as string | undefined
		if (tbyb === 'otp') return items
		const core = this.getCoreItemWithParentVariantId(items)
		if (!core) return items
		const refill = this.bundleConfigByRefillVariantId[core.id]
		const refillId = Number(refill.bundleChildren[0].refill_variant_id)
		if (!refillId) return items
		return [
			...items.filter((item) => item.id != core.id),
			{
				id: refillId,
				selling_plan: Number(refill.bundleChildren[0].selling_plan_id),
				quantity: Number(core.quantity ?? 0),
				properties: {
					_parent_variant_id: core.id,
				},
			},
			{
				id: core.id,
				quantity: core.quantity ?? 0,
				selling_plan: this.disableSellingPlanForRefill
					? undefined
					: core?.selling_plan,
				properties: {
					_refill_variant_id: refillId,
				},
			},
		]
	}
	addUpsellTracking(
		items: CartItem[],
		variantToTrack: string,
		newVariantId: string
	): CartItem[] {
		const newVariantIdInt = parseInt(newVariantId)
		const newCartItem = items.find((item) => item.id === newVariantIdInt)
		if (!newCartItem) {
			return items
		}

		return [
			...items.filter((item) => item.id !== newVariantIdInt),
			{
				...newCartItem,
				properties: {
					...(newCartItem.properties ?? {}),
					_upsell_from_variant: parseInt(variantToTrack),
				},
			},
		]
	}
}
